import type { ReactNode } from 'react';

import Icon from '@/components/public/ui/Icon';

type CalloutVariant = 'info' | 'success' | 'warning' | 'error';

interface CalloutProps {
  variant?: CalloutVariant;
  title: string;
  children?: ReactNode; // optional description body
  icon?: string; // override the default icon for the variant
}

// «Granito & Ouro» tinted-panel note: a token-based panel (variant colour at 8%
// fill, 30% border) with the icon in the variant colour. Shares the exact tonal
// system as `components/ui/Alert` so notices read the same across the site and
// admin. No glass, blur or gradient — those belonged to the retired dark look.
const VARIANT_TONES: Record<CalloutVariant, { icon: string; panel: string; iconColor: string }> = {
  info: {
    icon: 'info',
    panel: 'bg-primary/[0.08] border-primary/30',
    iconColor: 'text-primary',
  },
  success: {
    icon: 'verified_user',
    panel: 'bg-tertiary/[0.08] border-tertiary/30',
    iconColor: 'text-tertiary',
  },
  warning: {
    icon: 'warning',
    panel: 'bg-secondary/[0.08] border-secondary/30',
    iconColor: 'text-secondary',
  },
  error: {
    icon: 'error',
    panel: 'bg-error/[0.08] border-error/30',
    iconColor: 'text-error',
  },
};

export function Callout({ variant = 'info', title, children, icon }: CalloutProps) {
  const v = VARIANT_TONES[variant];
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 md:p-5 ${v.panel}`}>
      <span className={`${v.iconColor} mt-0.5 shrink-0`}>
        <Icon name={icon ?? v.icon} size="sm" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-on-surface text-sm font-semibold">{title}</p>
        {children && <div className="text-on-surface-light mt-1 text-sm">{children}</div>}
      </div>
    </div>
  );
}
